import { ResultOf } from "gql.tada";
import { DescriptionFillSectionById } from "@queries/entries/DescriptionFillSectionById";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { richTextToPlainText } from "@utils/richTextToPlainText";
import { useRef } from "react";
import { useScroll, useTransform } from "motion/react";
import cx from "classnames";
import { useTheme } from "@hooks/useTheme";
import { MarkdownLite } from "@components/shared/MarkdownLite";

export const DescriptionFillSection = ({
  entry: { descriptionFillSection: entry },
}: {
  entry: ResultOf<typeof DescriptionFillSectionById>;
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const value = useTransform(scrollYProgress, [0, 0.7], [-1, 0]);

  const { theme } = useTheme();

  const isNeutralForeground = theme.foreground.startsWith("neutral-");

  if (!entry) return null;

  return (
    <div className="flex flex-col dsk:flex-row py-20 dsk:py-36" ref={sectionRef}>
      <div className="grid-container">
        <h1 className="text-brand-cream dsk:col-start-2 col-span-5 dsk:mb-0 mb-10">
          <MarkdownLite>{entry.title}</MarkdownLite>
        </h1>
        <div
          className={cx(
            "dsk:col-start-7 col-span-full",
            isNeutralForeground
              ? "text-palette-current-foreground"
              : "text-palette-current-highlight"
          )}
        >
          <p className="text-body-feature">
            <AnimatedSplitLines
              value={value}
              animationType="clipPathReveal"
              underlyingTextClassNames={
                isNeutralForeground ? "opacity-30" : "text-palette-current-deep"
              }
            >
              {richTextToPlainText(entry.descriptionRichText?.json)}
            </AnimatedSplitLines>
          </p>
        </div>
      </div>
    </div>
  );
};